import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Minus, Trophy, Crown, Users, MapPin, CalendarDays, Globe, Users2 } from 'lucide-react';
import { SponsorBanner } from '@/components/SponsorBanner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TGA_LOGO = "https://customer-assets.emergentagent.com/job_853d16bb-77c9-4e1d-85ff-db52f6af924e/artifacts/lsv7codb_Copy%20of%20TGA%20pro%20tour%20logo%20transparent.png";

export const CreateTournamentPage = () => {
  const navigate = useNavigate();
  const { member } = useAuth();
  const [loading, setLoading] = useState(false);
  
  const [form, setForm] = useState({
    name: '',
    description: '',
    num_holes: '3',
    tournament_type: 'free',
    team_format: '',
    venue_name: '',
    venue_address: '',
    venue_maps_link: '',
    event_date: '',
    tour_id: '',
    season_id: '',
    allow_paid_entry: false
  });

  const [tours, setTours] = useState([]);
  const [seasons, setSeasons] = useState([]);

  useEffect(() => {
    axios.get(`${API_URL}/tours`).then(res => setTours(res.data)).catch(() => {});
    axios.get(`${API_URL}/seasons`).then(res => setSeasons(res.data)).catch(() => {});
  }, []);

  const [holes, setHoles] = useState([
    { hole_number: 1, par: 3 },
    { hole_number: 2, par: 3 },
    { hole_number: 3, par: 3 }
  ]);

  const handleHolesChange = (numHoles) => {
    const num = parseInt(numHoles);
    setForm({ ...form, num_holes: numHoles });
    
    const newHoles = [];
    for (let i = 1; i <= num; i++) {
      const existing = holes.find(h => h.hole_number === i);
      newHoles.push(existing || { hole_number: i, par: 3 });
    }
    setHoles(newHoles);
  };

  const updateHolePar = (holeNumber, delta) => {
    setHoles(holes.map(hole => {
      if (hole.hole_number === holeNumber) {
        const newPar = Math.max(1, Math.min(6, hole.par + delta));
        return { ...hole, par: newPar };
      }
      return hole;
    }));
  };

  const setHolePar = (holeNumber, par) => {
    setHoles(holes.map(hole => {
      if (hole.hole_number === holeNumber) {
        return { ...hole, par: parseInt(par) };
      }
      return hole;
    }));
  };

  const totalPar = holes.reduce((sum, hole) => sum + hole.par, 0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.name.trim()) {
      toast.error('Please enter a tournament name');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/tournaments?creator_id=${member.id}`, {
        name: form.name,
        description: form.description,
        num_holes: parseInt(form.num_holes),
        holes: holes,
        tournament_type: form.tournament_type,
        team_format: form.team_format || null,
        venue_name: form.venue_name || null,
        venue_address: form.venue_address || null,
        venue_maps_link: form.venue_maps_link || null,
        event_date: form.event_date ? new Date(form.event_date).toISOString() : null,
        tour_id: form.tour_id || null,
        season_id: form.season_id || null,
        allow_paid_entry: !!form.allow_paid_entry
      });
      
      toast.success('Tournament created successfully!');
      navigate(`/tournaments/${response.data.id}`);
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to create tournament';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Sponsor Banner - Top of Page */}
      <SponsorBanner variant="top-banner" />

      {/* Navigation */}
      <nav className="sticky top-0 z-50 glass border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost"
              onClick={() => navigate('/dashboard')}
              className="text-muted-foreground hover:text-white"
              data-testid="back-btn"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back
            </Button>
          </div>
          <img 
            src={TGA_LOGO} 
            alt="TGA Pro Tour" 
            className="h-10 object-contain"
          />
        </div>
      </nav>

      <main className="max-w-3xl mx-auto px-6 py-8">
        <div className="mb-8">
          <h1 className="text-3xl sm:text-4xl font-heading text-white mb-2">
            Create Tournament
          </h1>
          <p className="text-muted-foreground">
            Set up a new tournament with custom par values for each hole
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          <Card className="bg-card border-white/10 mb-6">
            <CardHeader>
              <CardTitle className="font-heading text-xl">Tournament Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Tournament Name</Label>
                <Input
                  id="name"
                  placeholder="e.g., Weekend Championship"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="bg-muted border-border focus:border-primary"
                  required
                  data-testid="tournament-name-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description (Optional)</Label>
                <Textarea
                  id="description"
                  placeholder="Add any details about the tournament..."
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className="bg-muted border-border focus:border-primary min-h-[80px]"
                  data-testid="tournament-description-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="num_holes">Number of Holes</Label>
                <Select 
                  value={form.num_holes} 
                  onValueChange={handleHolesChange}
                >
                  <SelectTrigger 
                    className="bg-muted border-border focus:border-primary w-full"
                    data-testid="num-holes-select"
                  >
                    <SelectValue placeholder="Select holes" />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-white/10">
                    <SelectItem value="3" data-testid="select-3-holes">3 Holes</SelectItem>
                    <SelectItem value="6" data-testid="select-6-holes">6 Holes</SelectItem>
                    <SelectItem value="9" data-testid="select-9-holes">9 Holes</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="tournament_type">Tournament Type</Label>
                <Select 
                  value={form.tournament_type} 
                  onValueChange={(value) => setForm({ ...form, tournament_type: value })}
                >
                  <SelectTrigger 
                    className="bg-muted border-border focus:border-primary w-full"
                    data-testid="tournament-type-select"
                  >
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-white/10">
                    <SelectItem value="free" data-testid="select-free-type">
                      <div className="flex items-center gap-2">
                        <Users className="w-4 h-4 text-muted-foreground" />
                        <span>Free / Demo</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="pro" data-testid="select-pro-type">
                      <div className="flex items-center gap-2">
                        <Crown className="w-4 h-4 text-amber-400" />
                        <span>Pro Tour (Pro Members Only)</span>
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-1">
                  {form.tournament_type === 'pro' 
                    ? 'Only Pro members can join this tournament' 
                    : 'Open to all members (Amateur & Pro)'}
                </p>
              </div>

              {form.tournament_type === 'pro' && (
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.allow_paid_entry}
                      onChange={(e) => setForm({ ...form, allow_paid_entry: e.target.checked })}
                      className="mt-1 w-4 h-4 accent-amber-400"
                      data-testid="allow-paid-entry-toggle"
                    />
                    <div>
                      <p className="text-white text-sm font-medium">Allow $10 pay-per-entry</p>
                      <p className="text-xs text-amber-200/70 mt-0.5">
                        Amateurs can pay $10 to join this tournament (includes a 30-day Pro trial). Leave off to keep this event Pro-members-only.
                      </p>
                    </div>
                  </label>
                </div>
              )}

              <div className="space-y-2">
                <Label>Game Mode</Label>
                <Select
                  value={form.team_format || 'individual'}
                  onValueChange={(value) => setForm({ ...form, team_format: value === 'individual' ? '' : value })}
                >
                  <SelectTrigger
                    className="bg-muted border-border focus:border-primary w-full"
                    data-testid="team-format-select"
                  >
                    <SelectValue placeholder="Individual" />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-white/10">
                    <SelectItem value="individual">
                      <div className="flex items-center gap-2">
                        <Users className="w-4 h-4 text-muted-foreground" />
                        <span>Individual</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="best_ball">
                      <div className="flex items-center gap-2">
                        <Users2 className="w-4 h-4 text-secondary" />
                        <span>Team — Best Ball</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="combined">
                      <div className="flex items-center gap-2">
                        <Users2 className="w-4 h-4 text-secondary" />
                        <span>Team — Combined Score</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="alternate_shot">
                      <div className="flex items-center gap-2">
                        <Users2 className="w-4 h-4 text-secondary" />
                        <span>Team — Alternate Shot</span>
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-1">
                  {form.team_format === 'best_ball' ? 'Two-person teams. Best (lowest) score on each hole counts.' :
                   form.team_format === 'combined' ? 'Two-person teams. Both scores on each hole are added together.' :
                   form.team_format === 'alternate_shot' ? 'Two-person teams. Players take turns scoring each hole.' :
                   'Standard individual scoring.'}
                </p>
              </div>

              {tours.length > 0 && (
                <div className="space-y-2">
                  <Label htmlFor="tour_id">Assign to Tour (Optional)</Label>
                  <Select
                    value={form.tour_id}
                    onValueChange={(value) => setForm({ ...form, tour_id: value === 'none' ? '' : value })}
                  >
                    <SelectTrigger
                      className="bg-muted border-border focus:border-primary w-full"
                      data-testid="tour-select"
                    >
                      <SelectValue placeholder="No tour assigned" />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-white/10">
                      <SelectItem value="none" data-testid="select-no-tour">
                        <span className="text-muted-foreground">No tour assigned</span>
                      </SelectItem>
                      {tours.map(tour => (
                        <SelectItem key={tour.id} value={tour.id} data-testid={`select-tour-${tour.id}`}>
                          <div className="flex items-center gap-2">
                            <Globe className="w-4 h-4 text-primary" />
                            <span>{tour.name}</span>
                            {tour.region && <span className="text-muted-foreground text-xs">({tour.region})</span>}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {seasons.length > 0 && (
                <div className="space-y-2">
                  <Label htmlFor="season_id">Assign to Season (Optional)</Label>
                  <Select
                    value={form.season_id}
                    onValueChange={(value) => setForm({ ...form, season_id: value === 'none' ? '' : value })}
                  >
                    <SelectTrigger
                      className="bg-muted border-border focus:border-primary w-full"
                      data-testid="season-select"
                    >
                      <SelectValue placeholder="No season assigned" />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-white/10">
                      <SelectItem value="none">
                        <span className="text-muted-foreground">No season assigned</span>
                      </SelectItem>
                      {seasons.map(season => (
                        <SelectItem key={season.id} value={season.id}>
                          <span>{season.name} {season.is_active ? '(Active)' : ''}</span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Event Schedule Card */}
          <Card className="bg-card border-white/10 mb-6">
            <CardHeader>
              <CardTitle className="font-heading text-xl flex items-center gap-2">
                <CalendarDays className="w-5 h-5 text-primary" />
                Event Schedule
              </CardTitle>
              <CardDescription>Optional - Add venue and date/time to show on the schedule</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="event_date">Event Date & Time</Label>
                <Input
                  id="event_date"
                  type="datetime-local"
                  value={form.event_date}
                  onChange={(e) => setForm({ ...form, event_date: e.target.value })}
                  className="bg-muted border-border focus:border-primary"
                  data-testid="event-date-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="venue_name">Venue Name</Label>
                <Input
                  id="venue_name"
                  placeholder="e.g., Pine Valley Golf Club"
                  value={form.venue_name}
                  onChange={(e) => setForm({ ...form, venue_name: e.target.value })}
                  className="bg-muted border-border focus:border-primary"
                  data-testid="venue-name-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="venue_address">Address</Label>
                <Input
                  id="venue_address"
                  placeholder="e.g., 123 Main St, City, State"
                  value={form.venue_address}
                  onChange={(e) => setForm({ ...form, venue_address: e.target.value })}
                  className="bg-muted border-border focus:border-primary"
                  data-testid="venue-address-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="venue_maps_link">Google Maps Link (Optional)</Label>
                <Input
                  id="venue_maps_link"
                  placeholder="https://maps.google.com/..."
                  value={form.venue_maps_link}
                  onChange={(e) => setForm({ ...form, venue_maps_link: e.target.value })}
                  className="bg-muted border-border focus:border-primary"
                  data-testid="venue-maps-link-input"
                />
                <p className="text-xs text-muted-foreground">
                  Paste a Google Maps link for the venue. If left empty, the address will auto-link to Google Maps.
                </p>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-card border-white/10 mb-6">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="font-heading text-xl">Hole Configuration</CardTitle>
                <div className="text-right">
                  <span className="text-muted-foreground text-sm">Total Par</span>
                  <p className="text-2xl font-mono font-bold text-primary">{totalPar}</p>
                </div>
              </div>
              <CardDescription>Set the par value for each hole (1-6)</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4">
                {holes.map((hole) => (
                  <div 
                    key={hole.hole_number}
                    className="flex items-center justify-between p-4 bg-muted rounded-lg border border-white/5"
                    data-testid={`hole-config-${hole.hole_number}`}
                  >
                    <div>
                      <span className="text-white font-heading text-lg">
                        Hole {hole.hole_number}
                      </span>
                    </div>
                    <div className="flex items-center gap-4">
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={() => updateHolePar(hole.hole_number, -1)}
                        disabled={hole.par <= 1}
                        className="score-btn-minus w-10 h-10 rounded-full"
                        data-testid={`hole-${hole.hole_number}-minus`}
                      >
                        <Minus className="h-4 w-4" />
                      </Button>
                      <div className="w-16 text-center">
                        <span className="text-sm text-muted-foreground">Par</span>
                        <p className="text-2xl font-mono font-bold text-white">{hole.par}</p>
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={() => updateHolePar(hole.hole_number, 1)}
                        disabled={hole.par >= 6}
                        className="score-btn-plus w-10 h-10 rounded-full"
                        data-testid={`hole-${hole.hole_number}-plus`}
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <div className="flex gap-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/dashboard')}
              className="flex-1 border-white/20 hover:bg-white/5 rounded-pill"
              data-testid="cancel-btn"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="flex-1 bg-primary hover:bg-primary/90 rounded-pill btn-hover-scale glow-purple"
              disabled={loading}
              data-testid="create-btn"
            >
              <Trophy className="mr-2 h-4 w-4" />
              {loading ? 'Creating...' : 'Create Tournament'}
            </Button>
          </div>
        </form>
      </main>
    </div>
  );
};
